import { Order, SharedProps } from '@/types'
import { formatCurrency, formatName } from '@/utils/format'
import { Head, router, usePage } from '@inertiajs/react'
import { ActionIcon, Card, Group, Paper, Stack, Table, Text, Title } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import dayjs from 'dayjs'

type ServiceBreakdownRow = {
  service_name: string
  count: number
  total: number
}

type SpendingsIndexProps = SharedProps & {
  month: string
  total: number
  order_count: number
  services_breakdown: ServiceBreakdownRow[]
  orders: Order[]
}

export default function SpendingsIndex({
  month,
  total,
  order_count,
  services_breakdown,
  orders,
}: SpendingsIndexProps) {
  const { current_organization } = usePage<SharedProps>().props

  const basePath = `/organizations/${current_organization?.slug}/spendings`

  const handleMonthChange = (value: string) => router.get(basePath, { month: value }, { preserveState: true })

  const handlePreviousMonth = () => handleMonthChange(dayjs(`${month}-01`).subtract(1, 'month').format('YYYY-MM'))

  const handleNextMonth = () => handleMonthChange(dayjs(`${month}-01`).add(1, 'month').format('YYYY-MM'))

  const handleOrderClick = (orderId: number) =>
    router.get(`/organizations/${current_organization?.slug}/orders/${orderId}`)

  const formatDate = (date: string) => dayjs(date).format('D MMM YYYY')
  const articlesLabel = (order: Order) => order.articles.map((article) => article.article_name).join(', ') || '—'

  return (
    <>
      <Head title={`Bilan Services - ${current_organization?.name}`} />

      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title>Bilan Services</Title>
          <Group gap={4}>
            <ActionIcon variant="subtle" color="gray" onClick={handlePreviousMonth} aria-label="Mois précédent">
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Text fw={500}>{dayjs(`${month}-01`).format('MMMM YYYY')}</Text>
            <ActionIcon variant="subtle" color="gray" onClick={handleNextMonth} aria-label="Mois suivant">
              <IconChevronRight size={20} />
            </ActionIcon>
          </Group>
        </Group>

        <Group grow align="stretch">
          <Card withBorder radius="md" padding="lg">
            <Text size="sm" c="dimmed">
              Total
            </Text>
            <Text size="xl" fw={700} mt={4}>
              {formatCurrency(total)}
            </Text>
          </Card>

          <Card withBorder radius="md" padding="lg">
            <Text size="sm" c="dimmed">
              Nombre de commandes
            </Text>
            <Text size="xl" fw={700} mt={4}>
              {order_count}
            </Text>
          </Card>
        </Group>

        <Title order={4} mt="md">
          Par service
        </Title>

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={500}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Service</Table.Th>
                  <Table.Th w={140}>Quantité</Table.Th>
                  <Table.Th w={160}>Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {services_breakdown.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Text c="dimmed" ta="center" py="md">
                        Aucune commande sur cette période
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {services_breakdown.map((row) => (
                  <Table.Tr key={row.service_name}>
                    <Table.Td fw={500}>{row.service_name}</Table.Td>
                    <Table.Td>{row.count}</Table.Td>
                    <Table.Td fw={600}>{formatCurrency(row.total)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              {services_breakdown.length > 0 && (
                <Table.Tfoot>
                  <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-dark-4)' }}>
                    <Table.Td fw={700}>Total</Table.Td>
                    <Table.Td fw={700}>{services_breakdown.reduce((acc, row) => acc + row.count, 0)}</Table.Td>
                    <Table.Td fw={700}>{formatCurrency(total)}</Table.Td>
                  </Table.Tr>
                </Table.Tfoot>
              )}
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {orders.length > 0 && (
          <>
            <Title order={4} mt="md">
              Commandes
            </Title>

            <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
              <Table.ScrollContainer minWidth={760}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={70}>N°</Table.Th>
                      <Table.Th>Client</Table.Th>
                      <Table.Th w={120}>Créée le</Table.Th>
                      <Table.Th w={120}>Envoyée le</Table.Th>
                      <Table.Th>Articles</Table.Th>
                      <Table.Th w={120}>Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {orders.map((order) => (
                      <Table.Tr key={order.id} onClick={() => handleOrderClick(order.id)} style={{ cursor: 'pointer' }}>
                        <Table.Td>#{order.id}</Table.Td>
                        <Table.Td>{formatName(order.customer)}</Table.Td>
                        <Table.Td>{formatDate(order.created_at)}</Table.Td>
                        <Table.Td>{order.dispatched_at ? formatDate(order.dispatched_at) : '—'}</Table.Td>
                        <Table.Td>{articlesLabel(order)}</Table.Td>
                        <Table.Td fw={600}>{formatCurrency(order.price)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>
          </>
        )}
      </Stack>
    </>
  )
}
